/**
 * RarityBadge - Displays MTG card rarity with standard MTG rarity colors
 *
 * Standard rarity colors:
 * - Common: muted silver (#a8a8b8 @ 80%)
 * - Uncommon: silver-white (#c0c0c0)
 * - Rare: gold (#f59e0b)
 * - Mythic: orange (#f97316)
 * - Special/Bonus: aether/purple (#6366f1)
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import type { Rarity } from '@/types/card';
import ManaSymbol from '../ManaSymbol/ManaSymbol';

export type RarityBadgeVariant = 'text' | 'chip' | 'chip-filter' | 'icon' | 'icon-text';

export interface RarityBadgeProps {
  rarity: Rarity;
  /** Display variant: 'text' (colored text), 'chip' (colored pill), 'chip-filter' (filter-style with active state) */
  variant?: RarityBadgeVariant;
  /** Whether the badge is in active/selected state (only for chip-filter variant) */
  isActive?: boolean;
  /** Optional click handler (only for chip-filter variant) */
  onClick?: () => void;
  /** Optional custom label (defaults to capitalized rarity) */
  label?: string;
  /** Optional className for additional styling */
  className?: string;
}

const RARITY_LABELS: Record<Rarity, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  mythic: 'Mythic',
  special: 'Special',
  bonus: 'Bonus',
};

/**
 * Get the CSS class for rarity text color
 */
function getRarityColorClass(rarity: Rarity): string {
  switch (rarity) {
    case 'common':
      return 'text-r-common!';
    case 'uncommon':
      return 'text-r-uncommon!';
    case 'rare':
      return 'text-r-rare!';
    case 'mythic':
      return 'text-r-mythic!';
    case 'special':
    case 'bonus':
    default:
      return 'text-r-other!';
  }
}

/**
 * Get the CSS classes for the chip background when active
 */
function getActiveChipClasses(rarity: Rarity): string {
  switch (rarity) {
    case 'common':
      return 'bg-r-common/15 text-r-common border border-r-common/25';
    case 'uncommon':
      return 'bg-r-uncommon/20 text-r-uncommon border border-r-uncommon/30';
    case 'rare':
      return 'bg-r-rare/20 text-r-rare border border-r-rare/30';
    case 'mythic':
      return 'bg-r-mythic/20 text-r-mythic border border-r-mythic/30';
    case 'special':
    case 'bonus':
    default:
      return 'bg-r-other/20 text-r-other border border-r-other/30';
  }
}

/**
 * Get the CSS classes for the chip in inactive state (subtle rarity coloring)
 */
function getInactiveChipClasses(rarity: Rarity): string {
  switch (rarity) {
    case 'common':
      return 'bg-slate text-r-common/60 hover:bg-storm hover:text-r-common/80 border-transparent';
    case 'uncommon':
      return 'bg-slate text-r-uncommon/70 hover:bg-storm hover:text-r-uncommon border-transparent';
    case 'rare':
      return 'bg-slate text-r-rare/70 hover:bg-storm hover:text-r-rare border-transparent';
    case 'mythic':
      return 'bg-slate text-r-mythic/70 hover:bg-storm hover:text-r-mythic border-transparent';
    case 'special':
    case 'bonus':
    default:
      return 'bg-slate text-r-other/70 hover:bg-storm hover:text-r-other border-transparent';
  }
}

export default function RarityBadge({
  rarity,
  variant = 'text',
  isActive = false,
  onClick,
  label,
  className,
}: RarityBadgeProps): ReactElement {
  const displayLabel = label ?? RARITY_LABELS[rarity];

  // Text variant - simple colored text
  if (variant === 'text') {
    return <span className={clsx(getRarityColorClass(rarity), className)}>{displayLabel}</span>;
  }

  // Chip variant - colored pill badge (non-interactive)
  if (variant === 'chip') {
    return (
      <span className={clsx('rounded-full px-2 py-0.5 text-xs font-medium', getActiveChipClasses(rarity), className)}>{displayLabel}</span>
    );
  }

  // Icon variant
  if (variant === 'icon') {
    return (
      <span title={rarity} className="leading-px">
        <ManaSymbol symbol="rarity" className={clsx(getRarityColorClass(rarity), className)} />
      </span>
    );
  }

  // Icon-text variant - icon with text label (for compact display with clearer meaning than color alone)
  if (variant === 'icon-text') {
    return (
      <span className={clsx('inline-flex items-center gap-1.5 font-medium', className)}>
        <span title={rarity} className="leading-px!">
          <ManaSymbol symbol="rarity" className={clsx(getRarityColorClass(rarity), className)} />
        </span>
        <span className={clsx(getRarityColorClass(rarity), 'text-sm')}>{displayLabel}</span>
      </span>
    );
  }

  // Chip-filter variant - interactive filter chip with active state
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
        'focus-visible:ring-aether focus-visible:ring-offset-void focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
        isActive ? getActiveChipClasses(rarity) : getInactiveChipClasses(rarity),
        className,
      )}
      aria-pressed={isActive}
      aria-label={`Filter by ${displayLabel} rarity`}
    >
      {displayLabel}
    </button>
  );
}
